import { Drawer as BaseDrawer } from '@base-ui/react/drawer'
import React from 'react'
import styled from 'styled-components'
import { Close } from 'sutra/components/Icons'
import { accents, semantic } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'
import { shadows } from 'sutra/tokens/shadows'
import { spacing } from 'sutra/tokens/spacing'
import { typography } from 'sutra/tokens/typography'

export type DrawerPlacement = 'left' | 'right'

export type DrawerChangeEventDetails = BaseDrawer.Root.ChangeEventDetails

export interface DrawerProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'title' | 'children'
> {
  open: boolean
  /**
   * Fires with `false` and the reason (`escape-key`, `outside-press`,
   * `close-press`, `swipe`...) when the user asks to close. The drawer stays
   * open until the caller sets `open` to `false`.
   */
  onOpenChange: (open: boolean, details: DrawerChangeEventDetails) => void
  /** Rendered in the header; labels the drawer for assistive tech. */
  title: React.ReactNode
  /** Announced as the drawer's description; rendered under the title. */
  description?: React.ReactNode
  children?: React.ReactNode
  /** Fixed (non-scrolling) row under the body. */
  footer?: React.ReactNode
  /** Which edge the drawer slides in from, and the swipe that dismisses it. */
  placement?: DrawerPlacement
  /** A CSS length; a bare number is pixels. */
  width?: number | string
  /** Keeps a press on the backdrop from closing; Escape and the close button still do. */
  disablePointerDismissal?: boolean
  /** Overlay z-index; antd dropdowns/selects opened from inside sit at 1050. */
  zIndex?: number
}

const Backdrop = styled(BaseDrawer.Backdrop)<{ $zIndex: number }>`
  position: fixed;
  inset: 0;
  z-index: ${({ $zIndex }) => $zIndex};
  background: rgba(10, 13, 18, 0.5);
  opacity: 1;
  transition: opacity ${motion.duration.base}ms ${motion.ease};

  &[data-starting-style],
  &[data-ending-style] {
    opacity: 0;
  }
`

// Fills the screen above Backdrop. Base UI treats a press on it (rather than
// on the popup inside) as a backdrop click.
const Viewport = styled(BaseDrawer.Viewport)<{ $zIndex: number }>`
  position: fixed;
  inset: 0;
  z-index: ${({ $zIndex }) => $zIndex};
`

const Popup = styled(BaseDrawer.Popup)<{
  $placement: DrawerPlacement
  $width: number | string
}>`
  --drawer-offscreen: ${({ $placement }) =>
    $placement === 'right' ? '100%' : '-100%'};

  position: absolute;
  top: 0;
  bottom: 0;
  ${({ $placement }) => $placement}: 0;
  display: flex;
  flex-direction: column;
  width: ${({ $width }) =>
    typeof $width === 'number' ? `${$width}px` : $width};
  max-width: 100vw;
  background: ${semantic.bg};
  box-shadow: ${shadows.xl};
  outline: none;
  transform: translateX(var(--drawer-swipe-movement-x, 0px));
  transition: transform ${motion.duration.slow}ms ${motion.ease};

  &[data-swiping] {
    transition-duration: 0ms;
  }

  &[data-starting-style],
  &[data-ending-style] {
    transform: translateX(var(--drawer-offscreen));
  }
`

// Base UI starts a swipe from a mouse press anywhere in the popup outside a
// Drawer.Content, which would also turn selecting text into a dismissal. The
// content fills the popup, so a drag on it is a selection; touch still swipes.
const Content = styled(BaseDrawer.Content)`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[4]}px;
  flex-shrink: 0;
  padding: ${spacing[4]}px ${spacing[6]}px;
  border-bottom: 1px solid ${semantic.border};
`

const Heading = styled.div`
  flex: 1;
  min-width: 0;
`

const Title = styled(BaseDrawer.Title)`
  margin: 0;
  font-size: ${typography.text.lg.fontSize}px;
  line-height: ${typography.text.lg.lineHeight}px;
  font-weight: ${typography.weights.semibold};
  color: ${semantic.fg};
  overflow-wrap: anywhere;
`

const Description = styled(BaseDrawer.Description)`
  margin: ${spacing[1]}px 0 0;
  font-size: ${typography.text.sm.fontSize}px;
  line-height: ${typography.text.sm.lineHeight}px;
  color: ${semantic.fgMuted};
`

const CloseButton = styled(BaseDrawer.Close)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  font-size: 16px;
  color: ${semantic.fgSubtle};
  background: transparent;
  border: none;
  border-radius: ${radii.xs}px;
  cursor: pointer;
  transition: color ${motion.duration.base}ms ${motion.ease};

  &:hover {
    color: ${semantic.fg};
  }
  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: 2px;
  }
`

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${spacing[6]}px;
`

const Footer = styled.div`
  flex-shrink: 0;
  padding: ${spacing[4]}px ${spacing[6]}px;
  border-top: 1px solid ${semantic.border};
`

export const Drawer = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  placement = 'right',
  width = 378,
  disablePointerDismissal = false,
  zIndex = 1000,
  ...popupProps
}: DrawerProps): React.ReactElement => (
  <BaseDrawer.Root
    open={open}
    onOpenChange={onOpenChange}
    swipeDirection={placement}
    disablePointerDismissal={disablePointerDismissal}
  >
    <BaseDrawer.Portal>
      <Backdrop $zIndex={zIndex} />
      <Viewport $zIndex={zIndex}>
        <Popup {...popupProps} $placement={placement} $width={width}>
          <Content>
            <Header>
              <Heading>
                <Title>{title}</Title>
                {description !== undefined && (
                  <Description>{description}</Description>
                )}
              </Heading>
              <CloseButton aria-label="Close">
                <Close />
              </CloseButton>
            </Header>
            <Body>{children}</Body>
            {footer !== undefined && <Footer>{footer}</Footer>}
          </Content>
        </Popup>
      </Viewport>
    </BaseDrawer.Portal>
  </BaseDrawer.Root>
)

Drawer.displayName = 'Drawer'
